const API_BASE = '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export type AccountStatus = 'pending' | 'approved' | 'banned';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'saas_owner' | 'saas_user';
  /** Omitted on legacy users is treated as approved. */
  accountStatus?: AccountStatus;
  onboarded: boolean;
  avatar?: string;
  timezone?: string;
  language?: string;
}

export interface WorkspaceMembership {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  workspaceLogo: string | null;
  workspaceRole: string;
  permissions: string[];
  planName: string | null;
  planCredits: number;
  creditBalance: number;
  renewsAt: string | null;
  /** Structured plan entitlement keys (gen.photo, ...) for feature gating. */
  planFeatures?: string[];
  /** Plan grants "Your Storage" (BYO-S3). */
  storageEnabled?: boolean;
  /** Display-only training-data flag from the plan. */
  dataUsedForTraining?: boolean;
  /** Private media expiry in days (0 = never). */
  mediaExpiryDays?: number;
}

export interface LoginResponse {
  twoFactorRequired: false;
  user: AuthUser;
  workspaces: WorkspaceMembership[];
  token: string;
}

export interface TwoFactorRequiredResponse {
  twoFactorRequired: true;
  tempToken: string;
}

export type LoginResult = LoginResponse | TwoFactorRequiredResponse;

export interface MeResponse {
  user: AuthUser;
  workspaces: WorkspaceMembership[];
}

async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
  const json = await res.json() as ApiResponse<T>;

  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Request failed');
  }

  return json.data;
}

export async function loginApi(email: string, password: string): Promise<LoginResult> {
  return apiFetch<LoginResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export interface Verify2FAResponse extends LoginResponse {
  twoFactorReset?: boolean;
}

export async function verify2FAApi(
  tempToken: string,
  payload: { code: string } | { recoveryCodes: string[] },
): Promise<Verify2FAResponse> {
  return apiFetch<Verify2FAResponse>('/auth/login/2fa', {
    method: 'POST',
    body: JSON.stringify({ tempToken, ...payload }),
  });
}

export interface WaitlistRegisterPayload {
  name: string;
  email: string;
  password: string;
  companyName: string;
  phone: string;
  phoneCountry?: string;
  phoneDialCode?: string;
  jobTitle: string;
  applicationNotes?: string;
  clientMetadata?: {
    timezone?: string;
    locale?: string;
    languages?: string[];
    referrer?: string;
    userAgent?: string;
    platform?: string;
    screen?: string;
  };
}

export async function registerApi(payload: WaitlistRegisterPayload): Promise<LoginResponse> {
  const result = await apiFetch<LoginResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return result;
}

export async function getMeApi(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/auth/me');
}

export interface CreateWorkspacePayload {
  name: string;
  slug: string;
  logo?: string;
  defaultCategory?: string;
  primaryColor?: string;
}

export async function createWorkspaceApi(data: CreateWorkspacePayload) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/workspaces`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Failed to create workspace');
  }
  return json.data;
}
